import React, { useState, useEffect } from "react";
import { useMutation } from "react-query";
import { useIntl } from "react-intl";
import Notiflix from "notiflix";

import { Checkbox, Button, Box } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PageTitle from "../common/PageTitle";
import BillLogo from "./shared/BillLogo";
import ApiRequest from "../../actions/ApiRequest";
import {
  getMadaBillsApiCalled,
  payMadaBillApiCalled,
} from "shared/endPoints/apiCallers";
import translate from "i18n/translate";
import { useHistory } from "react-router-dom";
import PayBillsMadaModal from "./PayBillsMadaModal";
import EditableTextFieldForm from "./shared/InputBills";
import DataTable from "./shared/DataTable";
import { calcTotalAmount } from "./shared/calcTotalAmount";
import GeneralPayModal from "./shared/GeneralPayModal";

const MadaBills = () => {
  const [number, setNumber] = useState("");
  const [identity, setIdentity] = useState("");
  const [selectedBill, setSelectedBill] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [bills, setBills] = useState([]);
  const [customerName, setCustomerName] = useState("");

  const history = useHistory();
  const intl = useIntl();

  useEffect(() => {
    const { state } = history.location;
    if (state?.number) {
      setNumber(state.number);
      setIdentity(state.identity);
      getMadaBills({ id: state.identity, number: state.number });
    }
  }, []);

  const {
    isLoading,
    mutate: getMadaBills,
    data: madaPills,
    reset,
  } = useMutation(getMadaBillsApiCalled, {
    onSuccess: (res) => {
      if (res?.status === "failed") {
        const errorReason = res?.reason ?? "Something went wrong";
        setErrorMessage(errorReason);
        return;
      }

      // Handle new response format - direct object with bills array
      if (res?.bills && Array.isArray(res.bills)) {
        if (res.success && res.bills.length > 0) {
          // Extract customer name from top level or first bill
          const customerNameFromResponse = res.customer_name || res.bills[0]?.customer_name || res.bills[0]?.additional_info?.customerName || '';
          setCustomerName(customerNameFromResponse);
          
          // Transform Mada bill data to match DataTable expectations
          const transformedBills = res.bills.map((bill, index) => ({
            id: bill.invoice_number || bill.biller_reference_id || `mada-${index}`,
            ref: bill.invoice_number || bill.biller_reference_id || `mada-${index}`,
            date: bill.issue_date ? new Date(bill.issue_date).toLocaleDateString() : '',
            must_paid: bill.due_date ? new Date(bill.due_date).toLocaleDateString() : '',
            amount: `${bill.amount || 0} ILS`, // Format as string for compatibility
            // Keep original bill data for payment processing
            originalBill: bill,
            // Additional fields for display
            description: bill.description || bill.name || '',
            customerName: bill.customer_name || bill.additional_info?.customerName || customerNameFromResponse,
            cycle: bill.cycle || ''
          }));
          
          setBills(transformedBills);
          setErrorMessage("");
          
          // Extract session_id from the first bill or use timestamp
          const sessionId = res.bills[0]?.session_id || res.timestamp;
          if (sessionId) {
            ApiRequest.post(
              `/restore_ab_company?company=mada&session_id=${sessionId}`
            );
          }
        } else {
          setBills([]);
          setErrorMessage(translate("No bills found for this service number"));
        }
      } else {
        // Fallback to old format for backward compatibility
        if (res?.length > 0) {
          setBills(res);
          ApiRequest.post(
            `/restore_ab_company?company=mada&session_id=${res[0].session_id}`
          );
        } else {
          setBills([]);
          setErrorMessage(translate("No bills found for this service number"));
        }
      }
    },
    onError: () => {
      Notiflix.Notify.failure(`Something went wrong !`, "", "Close");
    },
  });

  // Note: Bills are now handled in the onSuccess callback above
  // This useEffect is kept for backward compatibility if needed
  useEffect(() => {
    if (madaPills?.length > 0 && !madaPills?.bills) {
      setBills(madaPills);
    }
  }, [madaPills]);

  const onSubmit = (value) => {
    setNumber(value);
    setSelectedBill([]);
    setBills([]);
    setCustomerName(""); // Clear customer name when new enquiry
    getMadaBills({ id: identity, number: value });
  };

  const clearSelections = () => {
    setSelectedBill([]);
    reset();
  };

  const totalAmount = parseFloat(calcTotalAmount(selectedBill)?.toFixed(2));

  return (
    <div>
      <PageTitle title={translate("Mada Bills")} backPage="/pay_bills">
        <BillLogo
          src="https://speedtest.mada.ps/logo-v.png"
          alt="mada"
          style={{ marginTop: 20 }}
        />
        <EditableTextFieldForm
          initialValue={number}
          label={translate("Phone number")}
          onSubmit={onSubmit}
          isLoading={isLoading}
        />
        {bills?.length > 0 && (
          <>
            {customerName && (
              <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                <strong>{translate("Customer Name")}:</strong> {customerName}
              </Box>
            )}
            <div className="d-flex align-items-center justify-content-between mt-4">
              <p>
                {translate("Total Bills Amount")}:{""}
                <span className="text-info">
                  {totalAmount} {translate("ILS")}
                </span>
              </p>
              {/* <PayBillsMadaModal
                billSelected={selectedBill}
                number={number}
                isDisabled={!selectedBill?.amount || isLoading}
                clearSelections={clearSelections}
              /> */}
              <GeneralPayModal
                selectedBill={selectedBill}
                number={number}
                isDisabled={!totalAmount || isLoading}
                clearSelections={clearSelections}
                totalAmount={totalAmount}
                apiCall={payMadaBillApiCalled}
                provider="mada"
                showEditAmount
              />
            </div>
            <DataTable
              rows={bills}
              isLoading={isLoading}
              onRowSelect={setSelectedBill}
              selectedRows={selectedBill}
            />
          </>
        )}
        {bills?.length === 0 && !isLoading && number && <h5>{translate("No Results Found")}</h5>}
        {errorMessage && <h5 className="text-danger">{errorMessage}</h5>}
        
        <Box sx={{ display: 'flex', marginTop: 4, marginBottom: 2 }}>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push("/pay_bills")}
            sx={{ minWidth: 120 }}
          >
            {translate("Back")}
          </Button>
        </Box>
      </PageTitle>
    </div>
  );
};

export default MadaBills;
